import { useState } from "react";
import { CheckCircle2, CreditCard, X } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/shared/api/client";
import { fmtRubFull } from "@/shared/lib/format";
import { cn } from "@/shared/lib/cn";
import type { Me } from "@/shared/api/hooks";

const PRESETS = [10_000, 30_000, 50_000, 100_000, 300_000, 500_000];

const METHODS = [
  { id: "card", label: "Банковская карта", sub: "Visa, MasterCard, МИР" },
  { id: "transfer", label: "Расчётный счёт", sub: "Счёт на оплату по реквизитам" },
  { id: "tinkoff", label: "Tinkoff Business", sub: "Мгновенно для клиентов Тинькофф" },
];

export function TopUpModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [amount, setAmount] = useState<string>("");
  const [method, setMethod] = useState("card");
  const [done, setDone] = useState(false);
  const [newBalance, setNewBalance] = useState(0);
  const qc = useQueryClient();

  const topup = useMutation({
    mutationFn: (amt: number) =>
      api<Me>("/api/v1/account/topup", { method: "POST", body: JSON.stringify({ amount: amt, method }) }),
    onSuccess: (data) => {
      qc.setQueryData(["me"], data);
      qc.invalidateQueries({ queryKey: ["billing"] });
      setNewBalance(data.balance_rub);
      setDone(true);
    },
  });

  const parsed = parseInt(amount.replace(/\D/g, ""), 10);
  const valid = !isNaN(parsed) && parsed >= 1000 && parsed <= 10_000_000;

  function handleClose() {
    setDone(false);
    setAmount("");
    setMethod("card");
    onClose();
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] grid place-items-center p-4"
      style={{ background: "rgba(28,28,28,0.55)", backdropFilter: "blur(6px)" }}
      onClick={handleClose}
    >
      <div
        className="bg-[var(--bg)] rounded-xl shadow-xl overflow-hidden flex flex-col fade-up"
        style={{ width: "min(460px, 96vw)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-ink-12 flex-shrink-0 bg-[var(--bg-2)]">
          <span className="font-display font-bold text-[16px] tracking-tight">
            {done ? "Баланс пополнен" : "Пополнить баланс"}
          </span>
          <button
            onClick={handleClose}
            className="w-8 h-8 grid place-items-center rounded-md hover:bg-ink-6 text-ink-40 hover:text-ink"
          >
            <X size={16} />
          </button>
        </div>

        {done ? (
          <div className="px-6 py-10 flex flex-col items-center gap-4 text-center">
            <CheckCircle2 size={48} className="text-success" strokeWidth={1.5} />
            <div>
              <div className="font-display font-bold text-[20px] tracking-tight mb-1">
                +{fmtRubFull(parsed)}
              </div>
              <div className="text-ink-40 text-[13px]">
                Новый баланс: <span className="font-mono font-semibold text-ink">{fmtRubFull(newBalance)}</span>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="mt-2 h-10 px-6 rounded-pill bg-lime text-[var(--fg-on-accent)] font-display font-bold text-[13.5px] hover:[background:color-mix(in_srgb,var(--wb-lime)_88%,black)]"
            >
              Готово
            </button>
          </div>
        ) : (
          <div className="px-5 py-5 flex flex-col gap-5">
            {/* Amount */}
            <div>
              <div className="text-[11px] font-display font-semibold uppercase tracking-wider text-ink-40 mb-2">Сумма</div>
              <div className="flex flex-wrap gap-2 mb-3">
                {PRESETS.map((p) => (
                  <button
                    key={p}
                    onClick={() => setAmount(String(p))}
                    className={cn(
                      "h-8 px-3.5 rounded-pill border text-[12.5px] font-display font-semibold transition-colors",
                      parsed === p
                        ? "bg-ink text-white border-ink"
                        : "border-ink-12 text-ink-60 hover:border-ink/40 hover:text-ink",
                    )}
                  >
                    {fmtRubFull(p)}
                  </button>
                ))}
              </div>
              <div className="relative">
                <input
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/\D/g, ""))}
                  placeholder="Другая сумма, ₽"
                  className="w-full h-11 px-4 pr-10 rounded-md border border-ink-12 bg-white text-[14px] font-mono outline-none focus:border-ink"
                />
                {amount && (
                  <button
                    onClick={() => setAmount("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-40 hover:text-ink"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
              {amount && !valid && (
                <div className="text-[11.5px] text-danger mt-1.5">Минимальная сумма — 1 000 ₽, максимальная — 10 000 000 ₽</div>
              )}
            </div>

            {/* Payment method */}
            <div>
              <div className="text-[11px] font-display font-semibold uppercase tracking-wider text-ink-40 mb-2">Способ оплаты</div>
              <div className="flex flex-col gap-2">
                {METHODS.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => setMethod(m.id)}
                    className={cn(
                      "flex items-center gap-3 px-4 py-3 rounded-lg border text-left transition-colors",
                      method === m.id ? "border-ink bg-ink-6" : "border-ink-12 hover:border-ink/30",
                    )}
                  >
                    <div className={cn(
                      "w-4 h-4 rounded-full border-2 flex-shrink-0 grid place-items-center",
                      method === m.id ? "border-ink" : "border-ink-40",
                    )}>
                      {method === m.id && <div className="w-2 h-2 rounded-full bg-ink" />}
                    </div>
                    <CreditCard size={15} className="text-ink-40 flex-shrink-0" />
                    <div>
                      <div className="font-display font-semibold text-[13px]">{m.label}</div>
                      <div className="text-[11px] text-ink-40">{m.sub}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Submit */}
            <button
              onClick={() => valid && topup.mutate(parsed)}
              disabled={!valid || topup.isPending}
              className="h-11 rounded-pill bg-lime text-[var(--fg-on-accent)] font-display font-bold text-[14px] disabled:opacity-40 hover:[background:color-mix(in_srgb,var(--wb-lime)_88%,black)] transition-colors"
            >
              {topup.isPending ? "Обработка…" : valid ? `Пополнить на ${fmtRubFull(parsed)}` : "Пополнить"}
            </button>

            <div className="text-[11px] text-ink-30 text-center -mt-2">
              Это прототип — реальных транзакций нет. Баланс сбрасывается при демо-сбросе.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
